import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material"
import { FC, ReactNode } from "react"
import { Navigate, useNavigate, useParams } from "react-router"
import {
  MonthlyExtractManifestDto,
  MonthlyExtractRunDto,
  useGetMonthlyExtractRunQuery,
} from "src/features/things/monthlyExtractApi"
import {
  fmtDuration,
  fmtPeriod,
  isActive,
  MonthlyRunStatusChip,
  MonthlyRunWindowChip,
  RUN_POLL_MS,
  runWindow,
  WINDOW_LABEL,
} from "./monthlyReports/monthlyReportsChrome"
import { RunFilesPanel } from "./monthlyReports/RunFilesPanel"
import { SharePanel } from "./monthlyReports/SharePanel"
import { TabPreview } from "./monthlyReports/TabPreview"
import { fmtLocalDateTime, fmtNum } from "./reservationSync/ReservationSyncChrome"
import SaLayout from "./SaLayout"

/**
 * One monthly-extract run (LOCKER-469 DoD2): summary, files, per-tab preview and
 * the partner share link + PIN. Follows a pending / running run until it ends.
 */
export const SaMonthlyReportPage: FC = () => {
  const { runId = "" } = useParams()
  const navigate = useNavigate()
  const { data: first } = useGetMonthlyExtractRunQuery(runId)
  const active = !!first && isActive(first.data.run.status)
  const { data, isLoading, error } = useGetMonthlyExtractRunQuery(runId, {
    pollingInterval: active ? RUN_POLL_MS : 0,
  })
  const run = data?.data.run
  const manifest = data?.data.manifest ?? null

  return (
    <SaLayout selectedMenuItemLabelKey="monthlyReports">
      <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 3, md: 5 }, py: { xs: 4, md: 5 } }}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/sa/monthly-reports")}
          sx={{ mb: 1 }}
        >
          All runs
        </Button>
        {isLoading && <Typography variant="body2">loading run…</Typography>}
        {!!error && (
          <Alert severity="error">
            {(error as any)?.status === 404 ? `No run ${runId}.` : "could not load the run"}
          </Alert>
        )}
        {run && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography sx={{ fontSize: "1.3rem", fontWeight: 600 }}>
                {fmtPeriod(run.period)}
              </Typography>
              <MonthlyRunWindowChip window={runWindow(run)} />
              <MonthlyRunStatusChip status={run.status} />
              <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.disabled" }}>
                {run.id}
              </Typography>
            </Stack>
            {run.status === "failed" && run.error && (
              <Alert severity="error" data-testid="run-error">
                {run.error}
              </Alert>
            )}
            {data?.data.manifestError && (
              <Alert severity="warning">
                The manifest could not be read, so there is no preview: {data.data.manifestError}
              </Alert>
            )}
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
              <RunSummary run={run} manifest={manifest} />
              <Stack spacing={2} sx={{ flex: 1 }}>
                <SharePanel run={run} />
                <RunFilesPanel run={run} manifest={manifest} />
              </Stack>
            </Stack>
            {manifest && <TabPreview manifest={manifest} />}
          </Stack>
        )}
      </Box>
    </SaLayout>
  )
}

const Row: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <TableRow>
    <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap", width: 180 }}>{label}</TableCell>
    <TableCell>{children}</TableCell>
  </TableRow>
)

const RunSummary: FC<{ run: MonthlyExtractRunDto; manifest: MonthlyExtractManifestDto | null }> = ({
  run,
  manifest,
}) => {
  const s = manifest?.summary
  const warnings = Object.entries(s?.warnings ?? {}).filter(([, list]) => list.length > 0)
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1 }} data-testid="run-summary">
      <Typography sx={{ fontWeight: 600, mb: 1 }}>Summary</Typography>
      <Table size="small">
        <TableBody>
          <Row label="Window">
            {runWindow(run)} — {WINDOW_LABEL[runWindow(run)]}
          </Row>
          <Row label="Trigger">{run.trigger}</Row>
          <Row label="Requested by">{run.requestedBy}</Row>
          <Row label="Created">{fmtLocalDateTime(run.createdAt)}</Row>
          <Row label="Started">{run.startedAt ? fmtLocalDateTime(run.startedAt) : "—"}</Row>
          <Row label="Finished">{run.finishedAt ? fmtLocalDateTime(run.finishedAt) : "—"}</Row>
          <Row label="Duration">{fmtDuration(run.durationMs)}</Row>
          <Row label="Model">{run.model}</Row>
          {s && (
            <Row label="Window (UTC)">
              <span style={{ fontFamily: "monospace", fontSize: "0.78rem" }}>
                {s.window.startUtc} → {s.window.endUtc}
              </span>
            </Row>
          )}
          <Row label="Branches">{fmtNum(run.branches ?? s?.branches)}</Row>
          <Row label="Reservations">{fmtNum(run.reservations ?? s?.reservations)}</Row>
          {s && <Row label="Branches at capacity">{fmtNum(s.branchesAtCapacity)}</Row>}
          <Row label="Rows per tab">
            {run.rowCounts
              ? Object.entries(run.rowCounts).map(([tab, n]) => (
                  <div key={tab}>
                    {tab}: <strong>{fmtNum(n)}</strong>
                  </div>
                ))
              : "—"}
          </Row>
          {warnings.length > 0 && (
            <Row label="Warnings">
              {warnings.map(([name, list]) => (
                <div key={name}>
                  {name}: <strong>{list.length}</strong>
                </div>
              ))}
            </Row>
          )}
        </TableBody>
      </Table>
    </Paper>
  )
}

/**
 * `/sa/monthly-extracts/:runId` — the admin link LOCKER-468's Slack notice
 * posts (contract §5). Same page, under the route this ticket names.
 */
export const MonthlyExtractLinkRedirect: FC = () => {
  const { runId = "" } = useParams()
  return <Navigate to={`/sa/monthly-reports/${encodeURIComponent(runId)}`} replace />
}

export default SaMonthlyReportPage
